import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import Goback from "../../Components/goBack";
import { Button } from "../../Components/ui/button";
import { Modal, ModalContent } from "../../Components/ui/modal";
import Spinner from "../../Assets/spinner";
import { ProtectedRoutes } from "../../config/protectedRoutes";
import ZoneMapCanvas from "./components/ZoneMapCanvas";
import AreaChipList from "./components/AreaChipList";
import {
  useGetZoneByIdQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
} from "./ZoneApi";
import { areaUniquenessKey, uniqueAreas } from "./utils/areas";

const ZoneValidationSchema = yup.object().shape({
  name: yup.string().required("Zone name is required"),
  areas: yup.array().min(1, "Add at least one area to this zone"),
});

const emptyInitialValues = {
  name: "",
  areas: [],
};

const PENDING_AREA_DELETE_ID = "__pending_area__";

const getAreaId = (area, index) =>
  String(area?.id || area?._id || area?.areaId || `zone_area_${index}`);

const normalizeAreas = (areas = []) =>
  areas.map((area, index) => ({
    ...area,
    id: getAreaId(area, index),
  }));

const AddEditZone = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: existingZone, isLoading: loadingZone } = useGetZoneByIdQuery(
    id,
    { skip: !isEdit },
  );

  const [createZone, { isLoading: creating }] = useCreateZoneMutation();
  const [updateZone, { isLoading: updating }] = useUpdateZoneMutation();
  const saving = creating || updating;

  const [activeTool, setActiveTool] = useState(null);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [pendingArea, setPendingArea] = useState(null);
  const [deleteTargetAreaId, setDeleteTargetAreaId] = useState(null);
  const [mapStateVersion, setMapStateVersion] = useState(0);

  const formik = useFormik({
    initialValues: existingZone?.data
      ? {
          name: existingZone.data.name || "",
          areas: normalizeAreas(existingZone.data.areas),
        }
      : emptyInitialValues,
    enableReinitialize: true,
    validationSchema: ZoneValidationSchema,
    onSubmit: async (values) => {
      const areas = uniqueAreas(values.areas);
      const [firstArea] = areas;
      const payload = {
        name: values.name,
        // BE expects the first-added area repeated as the last item too.
        areas: firstArea ? [...areas, firstArea] : areas,
      };
      try {
        const data = isEdit
          ? await updateZone({ id, body: payload }).unwrap()
          : await createZone(payload).unwrap();
        if (data?.statusCode < 300) {
          toast.success("Zone saved successfully", { id: "zoneSaved" });
          navigate("/zones");
        }
      } catch (error) {
        toast.error("Could not save zone", { id: "zoneSaved" });
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    formik;

  const handleAreaConfirm = (name, latestPendingArea = pendingArea) => {
    if (!latestPendingArea) return;
    const areaId = String(latestPendingArea.tempId);
    const area = {
      id: areaId,
      name,
      longitude:
        latestPendingArea.centroid?.lng || latestPendingArea.longitude,
      latitude: latestPendingArea.centroid?.lat || latestPendingArea.latitude,
      source: latestPendingArea.source,
      boundary: latestPendingArea.boundary,
      centroid: latestPendingArea.centroid,
    };
    const areaKey = areaUniquenessKey(area);

    if (
      areaKey &&
      values.areas.some((item) => areaUniquenessKey(item) === areaKey)
    ) {
      toast.error(`${name} is already in this zone`, { id: "duplicateArea" });
      setPendingArea(null);
      setActiveTool(null);
      return;
    }

    setFieldValue("areas", [...values.areas, area]);
    setPendingArea(null);
    setSelectedAreaId(areaId);
    setActiveTool(null);
  };

  const handleAreaRemove = (areaId) => {
    const normalizedAreaId = String(areaId);
    setFieldValue(
      "areas",
      values.areas.filter((area) => String(area.id) !== normalizedAreaId),
    );
    if (selectedAreaId === normalizedAreaId) setSelectedAreaId(null);
  };

  const resetMapInteractionState = () => {
    setActiveTool(null);
    setSelectedAreaId(null);
    setPendingArea(null);
    setDeleteTargetAreaId(null);
    setMapStateVersion((version) => version + 1);
  };

  const handleAreaDeleteRequest = (areaId) => {
    if (areaId) {
      const normalizedAreaId = String(areaId);
      setDeleteTargetAreaId(normalizedAreaId);
      setSelectedAreaId(normalizedAreaId);
      return;
    }

    if (pendingArea) {
      setDeleteTargetAreaId(PENDING_AREA_DELETE_ID);
      return;
    }

    if (activeTool === "delete") {
      setActiveTool(null);
      return;
    }

    setActiveTool("delete");
    toast("Select the area you want to delete", { id: "zoneDeleteHint" });
  };

  const handleConfirmAreaDelete = () => {
    if (deleteTargetAreaId === PENDING_AREA_DELETE_ID) {
      setPendingArea(null);
    } else if (deleteTargetAreaId) {
      handleAreaRemove(deleteTargetAreaId);
    }
    resetMapInteractionState();
    toast.success("Area removed. Save the zone to persist changes.", {
      id: "zoneAreaRemoved",
    });
  };

  const handleAreaBoundaryChange = (areaId, { boundary, centroid }) => {
    setFieldValue(
      "areas",
      values.areas.map((a) =>
        String(a.id) === String(areaId) ? { ...a, boundary, centroid } : a,
      ),
    );
  };

  if (isEdit && loadingZone) {
    return (
      <div className='p-6'>
        <Goback currentRoute='Edit Zone' />
        <p className='text-sm text-primaryAsh'>Loading zone…</p>
      </div>
    );
  }

  const deleteTargetArea =
    deleteTargetAreaId === PENDING_AREA_DELETE_ID
      ? {
          name: pendingArea?.address || "this unsaved area",
        }
      : values.areas.find((area) => String(area.id) === deleteTargetAreaId);

  return (
    <FormikProvider value={formik}>
      <div className='flex flex-col h-[calc(100vh-100px)]'>
        <div className='px-6 bg-transparent'>
          <Goback currentRoute={isEdit ? "Edit Zone" : "Add Zone"} />
        </div>

        <div className='flex flex-1 min-h-0'>
          <div className='flex-[0_0_65%] min-w-0'>
            <ZoneMapCanvas
              areas={values.areas}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
              selectedAreaId={selectedAreaId}
              setSelectedAreaId={setSelectedAreaId}
              pendingArea={pendingArea}
              setPendingArea={setPendingArea}
              mapStateVersion={mapStateVersion}
              onResetMapState={resetMapInteractionState}
              onAreaBoundaryChange={handleAreaBoundaryChange}
              onAreaDeleteRequest={handleAreaDeleteRequest}
              onAreaConfirm={handleAreaConfirm}
            />
          </div>

          <div className='flex-[0_0_35%] border-l border-lightGray bg-white flex flex-col min-h-0'>
            <div className='flex-1 overflow-y-auto p-6'>
              <div className='mb-5'>
                <label className='block text-xs font-semibold text-HeaderColor mb-1.5'>
                  Zone Name
                </label>
                <input
                  id='name'
                  className='w-full px-3 py-2.5 text-sm border border-lightGray rounded-md outline-none focus:border-primaryGreen'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder='e.g. Zone 2 — Parliamentary'
                />
                {errors.name && touched.name && (
                  <p className='text-xs text-red mt-1'>{errors.name}</p>
                )}
              </div>

              <div className='h-px bg-lightGray my-5' />

              <p className='text-sm font-bold text-primaryBlack mb-3'>
                Areas in this zone ({uniqueAreas(values.areas).length})
              </p>
              <AreaChipList
                areas={values.areas}
                selectedAreaId={selectedAreaId}
                onSelect={(areaId) => {
                  // Same rule as selecting on the map: a stray unconfirmed
                  // pending draft is disposable once focus moves elsewhere.
                  if (pendingArea) setPendingArea(null);
                  setSelectedAreaId(selectedAreaId === areaId ? null : areaId);
                }}
                onRemove={handleAreaDeleteRequest}
              />
              {errors.areas && touched.areas && (
                <p className='text-xs text-red mt-2'>{errors.areas}</p>
              )}
            </div>

            <div className='flex-shrink-0 flex gap-2.5 p-5 border-t border-lightGray'>
              <Button
                type='button'
                variant='outline'
                className='flex-1'
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                type='button'
                className='flex-1'
                disabled={saving}
                onClick={formik.handleSubmit}
              >
                {saving ? <Spinner /> : "Save Zone"}
              </Button>
            </div>
          </div>
        </div>

        <Modal
          open={Boolean(deleteTargetAreaId)}
          onOpenChange={(open) => {
            if (!open) setDeleteTargetAreaId(null);
          }}
        >
          <ModalContent name='Delete Area?'>
            <p className='text-sm text-primaryBlack my-4'>
              Are you sure you want to delete{" "}
              <span className='font-semibold'>
                {deleteTargetArea?.name || "this area"}
              </span>
              ? This will remove the polygon from the zone.
            </p>
            <div className='flex justify-between items-center bg-white w-full'>
              <Button
                type='button'
                variant='outline'
                className='w-[45%]'
                onClick={() => setDeleteTargetAreaId(null)}
              >
                No
              </Button>
              <Button
                type='button'
                variant='failed'
                className='w-[45%]'
                onClick={handleConfirmAreaDelete}
              >
                Yes, Delete
              </Button>
            </div>
          </ModalContent>
        </Modal>
      </div>
    </FormikProvider>
  );
};

export default ProtectedRoutes(AddEditZone, [1, 2]);
